import React, { type FC } from "react";
import { BusinessList } from "@src/components/business-list";
import { ListItem } from "@src/components/business-list";
import { BusinessLotValue, BusinessMeasureValue, BusinessStockObjValue } from "@src/components/business-data-display";
import { OverflowMarquee } from "@src/shared/ui";
import { sumSpMovingQty } from "../../../../../../api/storePlaces";
import type { UseStockFinishedGoodsReceiptContentsQueryResult } from "../../hooks";
import {
    formatOrderText,
    formatPlacedCellsFromSpMovings,
    formatRecommendedCells,
} from "../../helpers/formatFinishedGoodsReceiptContent";

/** Пропсы компонента {@link StockFinishedGoodsReceiptContentsList}. */
interface StockFinishedGoodsReceiptContentsListProps {
    /** Query-result строк ВЗН УП. */
    contentsQuery: UseStockFinishedGoodsReceiptContentsQueryResult;
    /**
     * Открывает форму размещения продукции.
     * @param contentCode код строки ВЗН УП
     */
    onOpenContent: (contentCode: number) => void;
}

/** Список строк ВЗН УП для приемки готовой продукции на СГП. */
export const StockFinishedGoodsReceiptContentsList: FC<StockFinishedGoodsReceiptContentsListProps> = ({
    contentsQuery,
    onOpenContent,
}) => {
    const { content, isFetching } = contentsQuery;

    return (
        <BusinessList isLoad={isFetching} emptyText="Список строк ВЗН УП пуст">
            {content.map((item) => {
                const placedQty = sumSpMovingQty(item.SpMovings);
                const targetQty = item.ArrivalQty;
                const isFullyPlaced = targetQty > 0 && placedQty >= targetQty;

                return (
                    <ListItem
                        key={item.Code}
                        tone={isFullyPlaced ? "success" : "default"}
                        title={<BusinessStockObjValue stockObj={item.StockObj} mode="wrap" />}
                        props={[
                            {
                                label: "Партия прихода",
                                value: <BusinessLotValue lot={item.ArrivalLot} />,
                            },
                            {
                                label: "Заказ",
                                value: <OverflowMarquee>{formatOrderText(item.Order)}</OverflowMarquee>,
                            },
                            {
                                label: "Рекомендуемые ячейки",
                                value: <OverflowMarquee>{formatRecommendedCells(item.RecommendedCells)}</OverflowMarquee>,
                            },
                            {
                                label: "Размещено",
                                value: <BusinessMeasureValue value={`${placedQty} / ${targetQty}`} measure={item.Measure} />,
                            },
                            {
                                label: "Ячейки размещения",
                                value: <OverflowMarquee>{formatPlacedCellsFromSpMovings(item.SpMovings)}</OverflowMarquee>,
                            },
                        ]}
                        onClick={() => {
                            onOpenContent(item.Code);
                        }}
                    />
                );
            })}
        </BusinessList>
    );
};
